import type { AdminDashboardSectionProps } from '@admin/games/types';
import PageSection from '@admin/components/PageSection';
import MetricCard from '@admin/components/MetricCard';
import Breakdown from '@admin/components/Breakdown';
import type { WordClashDashboardMetrics } from '@games/word-clash/shared/types';

/** Compact integer formatting with thousands separators. */
function n(value: number): string {
  return value.toLocaleString();
}

/**
 * The dashboard's "Question bank" section (#817 — moved out of the framework
 * Dashboard). Reads the `questions` key Word Clash's server module adds to
 * `GET /admin/dashboard/metrics` (`GameModule.adminDashboardMetrics`); a
 * payload without it renders nothing rather than crashing the dashboard.
 */
export default function QuestionBankSection({ metrics }: AdminDashboardSectionProps) {
  const questions = (metrics as Partial<WordClashDashboardMetrics>).questions;
  if (!questions) return null;
  return (
    <PageSection
      title="Question bank"
      inset={false}
      testId="dashboard-section-questions"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          label="Total questions"
          value={n(questions.total)}
          testId="metric-questions-total"
          to="/questions?active=all"
          linkLabel={`Browse all ${n(questions.total)} questions`}
        />
        <MetricCard
          label="Active"
          value={n(questions.active)}
          sub={`${n(questions.activeEn)} EN · ${n(
            questions.activeTr,
          )} TR · ${n(questions.inactive)} inactive`}
          tone="positive"
          testId="metric-questions-active"
          to="/questions?active=active"
          linkLabel={`View the ${n(questions.active)} active questions`}
        />
        <MetricCard
          label="Unreviewed"
          value={n(questions.unreviewed)}
          sub="awaiting moderation"
          tone={questions.unreviewed > 0 ? 'warn' : 'default'}
          testId="metric-questions-unreviewed"
          to="/questions/review"
          linkLabel={`Review the ${n(questions.unreviewed)} unmoderated questions`}
        />
        <MetricCard
          label="By type"
          value={
            <Breakdown
              entries={questions.byType}
              testId="metric-questions-by-type"
            />
          }
          sub={
            <Breakdown
              entries={questions.byDifficulty}
              testId="metric-questions-by-difficulty"
            />
          }
          testId="metric-questions-breakdown"
        />
      </div>
    </PageSection>
  );
}
